/**
 * Table name for playbook definitions
 */
export const PLAYBOOK_TABLE = 'sys_pd_process_definition'

/**
 * API name for PlaybookDefinition function
 */
export const PLAYBOOK_API_NAME = 'PlaybookDefinition'

export const PLAYBOOK_PROCESS_TYPE_TABLE = 'sys_pd_process_type'

export const PLAYBOOK_PROCESS_TYPE_TO_DB = {
    'Standard playbook': '52c6d3eeff1be2102c2fffffffffff44',
} as const

export const PLAYBOOK_PROCESS_TYPE_FROM_DB: Readonly<Record<string, keyof typeof PLAYBOOK_PROCESS_TYPE_TO_DB>> = {
    [PLAYBOOK_PROCESS_TYPE_TO_DB['Standard playbook']]: 'Standard playbook',
}

export const PLAYBOOK_DEFAULT_PROCESS_TYPE_DB = PLAYBOOK_PROCESS_TYPE_TO_DB['Standard playbook']

export const PLAYBOOK_KNOWN_PROCESS_TYPE_NAMES = Object.keys(
    PLAYBOOK_PROCESS_TYPE_TO_DB
) as (keyof typeof PLAYBOOK_PROCESS_TYPE_TO_DB)[]

export function getPlaybookProcessTypeSysId(processType: string): string | undefined {
    return PLAYBOOK_PROCESS_TYPE_TO_DB[processType as keyof typeof PLAYBOOK_PROCESS_TYPE_TO_DB]
}

export function getPlaybookProcessTypeIdentifier(sysId: string): string | undefined {
    return PLAYBOOK_PROCESS_TYPE_FROM_DB[sysId]
}

export function isPlaybookProcessTypeSysId(value: string): boolean {
    return /^[0-9a-f]{32}$/i.test(value)
}

/**
 * Default values for playbook fields
 */
export const PLAYBOOK_DEFAULTS = {
    ACTIVE: true,
    ACCESS: 'public',
    STATUS: 'draft',
    RESTARTABLE: 'RESTARTABLE_FALSE',
    ALLOW_AS_NESTED: false,
    PUBLIC_ACCESS: false,
    RUN_STRATEGY: 'run_once',
    RUN_STRATEGY_ON_PROCESS_DEFINITION: true,
    EXECUTION_TYPE: 'record_driven',
    PROCESS_TYPE: 'Standard playbook',
    SCHEMA_VERSION: 3,
    /** Maximum schema version Fluent supports; used as the default when none is specified. */
    MAX_SUPPORTED_SCHEMA_VERSION: 3,
    START_NON_BLOCKING: true,
    DATA_RETENTION_PERIOD_OVERRIDE: '6_week',
    VIEW_TYPE: 'DIAGRAM',
    PERMISSION: '{}',
} as const

/**
 * Table name for playbook outputs
 */
export const PLAYBOOK_OUTPUT_TABLE = 'sys_pd_process_output'

/**
 * Element mapping provider attribute for playbook inputs.
 * Required by the PAD designer UI — a before-insert business rule normally stamps this,
 * but XML imports may skip business rules, so we set it explicitly.
 */
export const PLAYBOOK_INPUT_ATTRIBUTES =
    'element_mapping_provider=com.snc.pd.designer.elementmapping.PlaybookInputElementMapper'

/**
 * Table name for lane
 */
export const LANE_TABLE = 'sys_pd_lane'

/**
 * API name for Lane function
 */
export const LANE_API_NAME = 'Lane'

/**
 * Namespaced callee emitted for lanes in the toShape (database → Fluent) direction.
 */
export const LANE_GENERATED_CALLEE = 'wfa.playbook.lane'

/**
 * Default values for lane fields
 */
export const LANE_DEFAULTS = {
    ACTIVE: true,
    RESTART_RULE: 'RUN_ONLY_ONCE',
    ORDER: 1,
    PERMISSION: '{}',
    START_RULE: 'immediate',
    CONDITION_TO_RUN: '',
    STARTS_AFTER_ACTIVITIES: '',
    STARTS_AFTER_BRANCHES: '',
    STARTS_AFTER_LANES: '',
    PROCESS_DEFINITION: '',
    NAME: '',
} as const

/**
 * Table name for timer attributes (start with delay)
 */
export const TIMER_ATTRIBUTES_TABLE = 'sys_pd_timer_attributes'

/**
 * Maps Fluent API duration type names to database values.
 */
export const DURATION_TYPE_TO_DB = {
    explicit: 'explicit_duration',
    relative: 'relative_duration',
    percentage: 'percentage_duration',
} as const

/**
 * Maps database duration type values to Fluent API names.
 */
export const DURATION_TYPE_FROM_DB: Record<string, 'explicit' | 'relative' | 'percentage'> = {
    explicit_duration: 'explicit',
    relative_duration: 'relative',
    percentage_duration: 'percentage',
}

/**
 * Table name for activity
 */
export const ACTIVITY_TABLE = 'sys_pd_activity'

/**
 * API name for Activity function
 */
export const ACTIVITY_API_NAME = 'Activity'

/**
 * Namespaced callee emitted for activities in the toShape (database → Fluent) direction.
 */
export const ACTIVITY_GENERATED_CALLEE = 'wfa.playbook.activity'

/**
 * Table name for variable values (inputs and type vals)
 */
export const SYS_VARIABLE_VALUE_TABLE = 'sys_variable_value'

/**
 * Table name for element mappings (pill references for inputs and experience properties)
 */
export const SYS_ELEMENT_MAPPING_TABLE = 'sys_element_mapping'

/**
 * Default values for activity fields
 */
export const ACTIVITY_DEFAULTS = {
    ACTIVE: true,
    START_RULE: 'immediate',
    AI_AGENT_RUN_AS: 'playbook_user',
} as const

/** Platform-internal search token mapped to the human-readable 'keywords' field name */
export const KEYWORDS_PLATFORM_TOKEN = '123TEXTQUERY321'

/**
 * Regex matching `keywords` only in *field position* (start of a condition segment).
 * Segments are delimited by `^` or `^OR`, so the word must appear right after one of
 * those delimiters (or at the very start of the string).
 */
const KEYWORDS_FIELD_REGEX = /(?<=^|\^(?:OR)?)keywords\b/g

/**
 * Same positional constraint but for the platform token (decode direction).
 */
const KEYWORDS_TOKEN_FIELD_REGEX = new RegExp(`(?<=^|\\^(?:OR)?)${KEYWORDS_PLATFORM_TOKEN}\\b`, 'g')

/**
 * Build direction: replace the human-readable `keywords` field name with the
 * platform-internal token when it appears in field position inside a condition string.
 */
export function encodeKeywordsToken(condition: string): string {
    return condition.replace(KEYWORDS_FIELD_REGEX, KEYWORDS_PLATFORM_TOKEN)
}

/**
 * Download direction: replace the platform-internal token back to the human-readable
 * `keywords` field name when it appears in field position inside a condition string.
 */
export function decodeKeywordsToken(condition: string): string {
    return condition.replace(KEYWORDS_TOKEN_FIELD_REGEX, 'keywords')
}

/**
 * Table name for trigger instances
 */
export const TRIGGER_INSTANCE_TABLE = 'sys_pd_trigger_instance'

/**
 * Table name for playbook process variants
 */
export const VARIANT_TABLE = 'sys_pd_process_variant'

/**
 * API name for wfa.playbook.trigger() calls
 */
export const TRIGGER_INSTANCE_API_NAME = 'wfa.playbook.trigger'

/**
 * Global alias for PlaybookTrigger() calls (mirrors Lane/wfa.playbook.lane pattern)
 */
export const TRIGGER_INSTANCE_GLOBAL_NAME = 'PlaybookTrigger'

/**
 * All valid callee names for trigger instance calls
 */
export const TRIGGER_INSTANCE_CALLEE_NAMES = [TRIGGER_INSTANCE_API_NAME, TRIGGER_INSTANCE_GLOBAL_NAME]

/**
 * Table name for playbook process inputs
 */
export const PROCESS_INPUT_TABLE = 'sys_pd_process_input'

/**
 * Fixed field values for the auto-generated parent_record process input.
 * These match the platform's behavior when setting Parent Table in the UI.
 */
export const PARENT_RECORD = {
    ELEMENT: 'parent_record',
    LABEL: 'Parent record',
    INTERNAL_TYPE: 'reference',
    MAX_LENGTH: 32,
    ORDER: 1,
    /** Platform-specific attribute that enables the playbook input element mapper */
    ATTRIBUTES: 'element_mapping_provider=com.snc.pd.designer.elementmapping.PlaybookInputElementMapper',
} as const

/**
 * The Fluent-facing camelCase key for the parent record in trigger mapper callbacks
 * and shape reconstruction. The corresponding platform field name is PARENT_RECORD.ELEMENT.
 */
export const PARENT_RECORD_MAPPER_KEY = 'parentRecord'

/**
 * Callee name for wfa.playbook.dataPill() calls
 */
export const DATAPILL_CALLEE = 'wfa.playbook.dataPill'

/**
 * Callee name for wfa.playbook.currentActivity references
 */
export const CURRENT_ACTIVITY_CALLEE = 'wfa.playbook.currentActivity'

/**
 * All valid callee names for Activity calls
 */
export const ACTIVITY_CALLEE_NAMES = new Set([ACTIVITY_API_NAME, 'wfa.playbook.activity'])

/**
 * All valid callee names for Lane calls
 */
export const LANE_CALLEE_NAMES = new Set([LANE_API_NAME, 'wfa.playbook.lane'])

/** Platform suffix appended to pill paths to request the table name of a record reference. */
export const TABLE_NAME_SUFFIX = '.:TN'
